import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  pt: {
    // Navigation
    nav: {
      about: 'Sobre',
      services: 'Serviços',
      portfolio: 'Portfolio',
      clients: 'Clientes',
      contact: 'Contacto',
      plans: 'Planos',
      bookMeeting: 'Agendar reunião',
      bookCall: 'Agendar chamada',
      bookStrategy: 'Agendar estratégia',
      bookStrategyMicro: 'Sem compromisso. Resposta em até 48h.',
      form: 'Formulário',
    },
    
    // Hero
    hero: {
      badge: 'Agência Criativa · Tavira, Portugal',
      headline: 'Gestão de redes sociais e criação de conteúdo para marcas que querem crescer com',
      headlineAccent: 'consistência',
      subheadline: 'Estratégia, conteúdo e materiais criativos para negócios que querem comunicar melhor no digital.',
      cta: 'Agendar estratégia',
      ctaMicro: 'Sem compromisso. Resposta em até 48h.',
      secondaryCta: 'Ver serviços',
      trustedBy: 'Aprovado por marcas locais e internacionais',
    },
    
    // About
    about: {
      badge: 'Sobre a Agência',
      title: 'Tecemos narrativas que conectam marcas a pessoas',
      description: 'A Loom Agency é uma agência criativa focada em ajudar negócios a comunicar de forma autêntica e consistente no digital. Não prometemos resultados explosivos ou números inflacionados — acreditamos no crescimento orgânico, sustentável e transparente.',
      features: [
        {
          title: 'Gestão de Redes Sociais',
          description: 'Planeamento estratégico e publicação consistente nas principais plataformas.',
        },
        {
          title: 'Captação de Conteúdo',
          description: 'Fotografia e vídeo profissional para comunicar a essência da sua marca.',
        },
        {
          title: 'Materiais de Divulgação',
          description: 'Design gráfico para campanhas, menus, flyers e materiais digitais.',
        },
      ],
    },
    
    // Services
    services: {
      badge: 'O Que Fazemos',
      title: 'Serviços pensados para o crescimento da sua marca',
      description: 'Oferecemos um conjunto completo de serviços para ajudar a sua marca a comunicar de forma eficaz e consistente no mundo digital.',
      items: [
        {
          title: 'Gestão de Redes Sociais',
          description: 'Estratégia completa para as suas redes sociais. Planeamento de conteúdo, publicação consistente e análise de métricas para garantir presença digital forte.',
          benefits: ['Planeamento mensal', 'Publicação diária', 'Relatórios de desempenho'],
        },
        {
          title: 'Captação de Conteúdo',
          description: 'Produção profissional de fotografia e vídeo para redes sociais, website e campanhas. Conteúdo visual que representa a identidade da sua marca.',
          benefits: ['Fotografia profissional', 'Vídeo para redes sociais', 'Edição e pós-produção'],
        },
        {
          title: 'Materiais de Divulgação',
          description: 'Design gráfico para todos os materiais da sua comunicação. De menus a flyers, cartões de visita a banners digitais.',
          benefits: ['Design gráfico', 'Materiais impressos', 'Assets digitais'],
        },
      ],
      learnMore: 'Saber mais',
    },
    
    // Work Preview
    workPreview: {
      badge: 'O Nosso Trabalho',
      title: 'Veja o nosso trabalho em ação',
      description: 'Descubra como ajudámos marcas a crescer através de conteúdo estratégico para redes sociais e storytelling digital.',
      cta: 'Ver Portfolio',
    },
    
    // Clients
    clients: {
      badge: 'Prova Social',
      title: 'Marcas que confiam na Loom',
      description: 'Trabalhamos com negócios de diferentes sectores, desde restauração a turismo, imobiliário a beleza. O que têm em comum? A vontade de crescer com consistência.',
      categories: {
        restaurant: 'Restauração',
        tourism: 'Turismo',
        art: 'Arte & Cultura',
        beauty: 'Beleza',
        services: 'Serviços',
        realestate: 'Imobiliário',
      },
      stats: {
        activeClients: 'Clientes ativos',
        postsMonth: 'Publicações/mês',
        yearsExperience: 'Anos de experiência',
      },
    },
    
    // Differentiator
    differentiator: {
      badge: 'O Nosso Diferencial',
      title: 'Crescimento real, sem promessas vazias.',
      description: 'Na Loom Agency, não vendemos ilusões. Trabalhamos com foco em resultados reais e sustentáveis, construídos através de estratégia, criatividade e comunicação transparente com cada cliente.',
      grewWithUs: 'Clientes que cresceram connosco',
      values: [
        {
          title: 'Consistência',
          description: 'Resultados sustentáveis que se constroem com tempo e dedicação, não com promessas vazias.',
        },
        {
          title: 'Criatividade',
          description: 'Cada marca é única. Criamos conteúdo personalizado que reflete a verdadeira essência do seu negócio.',
        },
        {
          title: 'Estratégia',
          description: 'Não publicamos por publicar. Cada ação é pensada para atingir os objetivos da sua marca.',
        },
        {
          title: 'Transparência',
          description: 'Comunicação clara e honesta. Mostramos o que funciona e ajustamos o que precisa de melhoria.',
        },
      ],
    },
    
    // CTA
    cta: {
      badge: 'Vamos conversar',
      title: 'Vamos falar sobre o teu projeto?',
      description: 'Marca uma reunião sem compromisso e vamos descobrir juntos como podemos ajudar a tua marca a comunicar melhor no digital.',
      button: 'Agendar estratégia',
      buttonMicro: 'Sem compromisso. Resposta em até 48h.',
    },
    
    // Contact
    contact: {
      badge: 'Contacto',
      title: 'Vamos criar algo incrível juntos',
      description: 'Estamos prontos para ouvir as tuas ideias e ajudar a transformá-las em comunicação digital eficaz. Entra em contacto através de qualquer um dos canais abaixo.',
      labels: {
        email: 'Email',
        phone: 'Telefone',
        instagram: 'Instagram',
        location: 'Localização',
      },
      locationBadge: 'Algarve · Costa Sul',
    },
    
    // Footer
    footer: {
      tagline: 'Agência criativa especializada em gestão de redes sociais, captação de conteúdo e materiais de divulgação.',
      navigation: 'Navegação',
      contact: 'Contacto',
      rights: 'Todos os direitos reservados.',
      madeIn: 'By B4 Digital em Tavira, Portugal',
    },
    
    // Portfolio Page
    portfolio: {
      badge: 'Portfolio',
      title: 'Resultados de Clientes',
      subtitle: 'Resultados reais de marcas que confiaram na nossa estratégia de redes sociais e criação de conteúdo.',
      ctaTitle: 'Queres resultados assim?',
      ctaDescription: 'Vamos conversar sobre como podemos fazer a tua marca crescer com conteúdo estratégico.',
      ctaButton: 'Marcar chamada estratégica',
      backHome: 'Voltar ao Início',
      projects: [
        {
          name: 'Maxi Pizza',
          industry: 'Restauração',
          description: 'Em 30 dias aumentámos o alcance em mais de 100% e gerámos forte engagement através de conteúdo autêntico que mostra a paixão por pizza artesanal.',
          tags: ['+100% alcance', '+250% engagement', '45 reels/mês'],
        },
        {
          name: 'Atlantis Tours',
          industry: 'Turismo',
          description: 'Criámos conteúdo visual impactante que captura a beleza das experiências no Algarve e atrai turistas de todo o mundo.',
          tags: ['+180% reservas', '500K visualizações', 'Audiência global'],
        },
        {
          name: 'The Art Studio Tavira',
          industry: 'Arte & Workshops',
          description: 'Mostramos o processo criativo por detrás dos workshops de cerâmica, conectando artistas locais com uma comunidade apaixonada.',
          tags: ['+300% workshops', 'Comunidade ativa', 'Turistas & locais'],
        },
        {
          name: 'Algarve Vacation',
          industry: 'Arrendamento de Férias',
          description: 'Fotografia e vídeo premium a destacar propriedades de luxo para férias em todo o Algarve.',
          tags: ['+85% ocupação', 'Reservas diretas', 'ROI positivo'],
        },
        {
          name: 'Au Comptoir Beauté',
          industry: 'Salão de Beleza',
          description: 'Conteúdo elegante de beleza desenhado para atrair clientes premium e construir uma presença de marca sofisticada.',
          tags: ['+200% seguidores', 'Agenda cheia', 'Clientela premium'],
        },
      ],
    },
    
    // Pricing Section
    pricing: {
      badge: 'Estratégia',
      title: 'Estratégia pensada para a tua marca',
      subtitle: '',
      customTitle: 'Solução à medida',
      cta: 'Criar a minha estratégia',
      paragraphs: [
        'Na Loom, não trabalhamos com planos fixos.',
        'Cada marca, empresa ou ideia tem um momento, um objetivo e uma necessidade diferente e é exatamente por isso que criamos <strong>soluções à medida de cada cliente</strong>.',
        'Em vez de escolheres um pacote, queremos perceber o teu negócio, os teus desafios e onde queres chegar.',
        'A partir daí, desenhamos uma estratégia clara, simples e adaptada à tua realidade.',
      ],
      bullets: [
        'Sem fórmulas genéricas.',
        'Sem soluções copiadas.',
        'Só direção pensada para crescer com consistência.',
      ],
      services: [
        'Estratégia de conteúdo',
        'Gestão de redes sociais',
        'Captação de conteúdo',
        'Campanhas pagas (se aplicável)',
        'Otimização contínua',
      ],
    },
    
    // Plans Page
    plansPage: {
      title: 'Escolhe como queres crescer no digital',
      subtitle: 'Cada plano foi pensado para uma fase diferente do teu negócio.',
      ctaTitle: 'Não tens a certeza de qual escolher?',
      ctaDescription: 'Falamos contigo e definimos o melhor caminho para a tua marca.',
      ctaButton: 'Agendar estratégia',
    },
    
    // Form Page
    form: {
      title: 'Vamos falar sobre a tua marca',
      subtitle: 'Preenche este formulário rápido para percebermos como te podemos ajudar a crescer com estratégia e criatividade.',
      timeNote: 'Leva menos de 2 minutos',
      next: 'Continuar',
      back: 'Voltar',
      submit: 'Enviar',
      steps: {
        name: 'Qual é o teu nome?',
        business: 'Qual é o nome da tua marca ou negócio?',
        social: 'Deixa o teu Instagram ou website',
        location: 'Onde estás localizado?',
        help: 'Com o que precisas de ajuda?',
        budget: 'Qual é o teu orçamento mensal para marketing?',
        frustration: 'Numa frase, o que mais te está a frustrar na tua presença digital neste momento?',
        result: 'Que resultado te faria dizer "valeu a pena"?',
        contact: 'Como podemos contactar-te?',
      },
      helpOptions: [
        'Criação de conteúdo',
        'Gestão de redes sociais',
        'Estratégia',
        'Branding e consistência',
        'Não sei — preciso de orientação',
      ],
      budgetOptions: [
        'Menos de 350€',
        '350€ – 800€',
        '800€ – 1500€',
        '1500€+',
      ],
      emailLabel: 'Qual é o teu email?',
      phoneLabel: 'Qual é o teu número de telefone?',
      thankYou: {
        title: 'Obrigado por confiares na Loom',
        message: 'Se a tua visão estiver alinhada com a nossa, vais receber uma resposta dentro de 48h com uma ideia personalizada — sem pressão, sem compromisso.',
        cta: 'Ver portfólio',
      },
    },
    
    // Admin Dashboard
    admin: {
      title: 'Dashboard',
      login: 'Entrar',
      logout: 'Sair',
      email: 'Email',
      password: 'Palavra-passe',
      loginTitle: 'Acesso ao Dashboard',
      stats: {
        visitsToday: 'Visitas hoje',
        totalVisits: 'Total de visitas',
        ctaClicks: 'Cliques no CTA',
        conversionRate: 'Taxa de conversão',
        totalLeads: 'Total de leads',
        avgTime: 'Tempo médio',
      },
      filters: {
        last7days: 'Últimos 7 dias',
        last30days: 'Últimos 30 dias',
      },
      leads: 'Leads',
      submissions: 'Submissões',
      viewDetails: 'Ver detalhes',
    },
  },
  
  en: {
    // Navigation
    nav: {
      about: 'About',
      services: 'Services',
      portfolio: 'Portfolio',
      clients: 'Clients',
      contact: 'Contact',
      plans: 'Plans',
      bookMeeting: 'Book a meeting',
      bookCall: 'Book a call',
      bookStrategy: 'Book a strategy call',
      bookStrategyMicro: 'No commitment. Response within 48h.',
      form: 'Form',
    },
    
    // Hero
    hero: {
      badge: 'Creative Agency · Tavira, Portugal',
      headline: 'Social media management and content creation for brands that want to grow with',
      headlineAccent: 'consistency',
      subheadline: 'Strategy, content, and creative materials for businesses that want to communicate better in the digital world.',
      cta: 'Book a strategy call',
      ctaMicro: 'No commitment. Response within 48h.',
      secondaryCta: 'View services',
      trustedBy: 'Trusted by local and international brands',
    },
    
    // About
    about: {
      badge: 'About the Agency',
      title: 'We weave narratives that connect brands to people',
      description: 'Loom Agency is a creative agency focused on helping businesses communicate authentically and consistently in the digital space. We don\'t promise explosive results or inflated numbers — we believe in organic, sustainable, and transparent growth.',
      features: [
        {
          title: 'Social Media Management',
          description: 'Strategic planning and consistent publishing across major platforms.',
        },
        {
          title: 'Content Creation',
          description: 'Professional photography and video to communicate your brand\'s essence.',
        },
        {
          title: 'Marketing Materials',
          description: 'Graphic design for campaigns, menus, flyers, and digital materials.',
        },
      ],
    },
    
    // Services
    services: {
      badge: 'What We Do',
      title: 'Services designed for your brand\'s growth',
      description: 'We offer a complete set of services to help your brand communicate effectively and consistently in the digital world.',
      items: [
        {
          title: 'Social Media Management',
          description: 'Complete strategy for your social media. Content planning, consistent publishing, and metrics analysis to ensure a strong digital presence.',
          benefits: ['Monthly planning', 'Daily publishing', 'Performance reports'],
        },
        {
          title: 'Content Creation',
          description: 'Professional photography and video production for social media, websites, and campaigns. Visual content that represents your brand\'s identity.',
          benefits: ['Professional photography', 'Social media video', 'Editing & post-production'],
        },
        {
          title: 'Marketing Materials',
          description: 'Graphic design for all your communication materials. From menus to flyers, business cards to digital banners.',
          benefits: ['Graphic design', 'Print materials', 'Digital assets'],
        },
      ],
      learnMore: 'Learn more',
    },
    
    // Work Preview
    workPreview: {
      badge: 'Our Work',
      title: 'See our work in action',
      description: 'Discover how we\'ve helped brands grow through strategic social media content and digital storytelling.',
      cta: 'View Portfolio',
    },
    
    // Clients
    clients: {
      badge: 'Social Proof',
      title: 'Brands that trust Loom',
      description: 'We work with businesses from different sectors, from restaurants to tourism, real estate to beauty. What do they have in common? The desire to grow with consistency.',
      categories: {
        restaurant: 'Restaurant',
        tourism: 'Tourism',
        art: 'Art & Culture',
        beauty: 'Beauty',
        services: 'Services',
        realestate: 'Real Estate',
      },
      stats: {
        activeClients: 'Active clients',
        postsMonth: 'Posts/month',
        yearsExperience: 'Years of experience',
      },
    },
    
    // Differentiator
    differentiator: {
      badge: 'Our Difference',
      title: 'Real growth, without empty promises.',
      description: 'At Loom Agency, we don\'t sell illusions. We work with a focus on real, sustainable results, built through strategy, creativity, and transparent communication with each client.',
      grewWithUs: 'Clients who grew with us',
      values: [
        {
          title: 'Consistency',
          description: 'Sustainable results built with time and dedication, not empty promises.',
        },
        {
          title: 'Creativity',
          description: 'Every brand is unique. We create personalised content that reflects your business\'s true essence.',
        },
        {
          title: 'Strategy',
          description: 'We don\'t post for the sake of it. Every action is designed to achieve your brand\'s goals.',
        },
        {
          title: 'Transparency',
          description: 'Clear and honest communication. We show what works and adjust what needs improvement.',
        },
      ],
    },
    
    // CTA
    cta: {
      badge: 'Let\'s chat',
      title: 'Shall we talk about your project?',
      description: 'Book a no-obligation meeting and let\'s discover together how we can help your brand communicate better in the digital world.',
      button: 'Book a strategy call',
      buttonMicro: 'No commitment. Response within 48h.',
    },
    
    // Contact
    contact: {
      badge: 'Contact',
      title: 'Let\'s create something amazing together',
      description: 'We\'re ready to hear your ideas and help transform them into effective digital communication. Get in touch through any of the channels below.',
      labels: {
        email: 'Email',
        phone: 'Phone',
        instagram: 'Instagram',
        location: 'Location',
      },
      locationBadge: 'Algarve · South Coast',
    },
    
    // Footer
    footer: {
      tagline: 'Creative agency specialising in social media management, content creation, and marketing materials.',
      navigation: 'Navigation',
      contact: 'Contact',
      rights: 'All rights reserved.',
      madeIn: 'By B4 Digital in Tavira, Portugal',
    },
    
    // Portfolio Page
    portfolio: {
      badge: 'Portfolio',
      title: 'Client Results',
      subtitle: 'Real results from brands that trusted our social media strategy and content creation.',
      ctaTitle: 'Want results like these?',
      ctaDescription: 'Let\'s talk about how we can grow your brand with strategic content.',
      ctaButton: 'Book a strategy call',
      backHome: 'Back to Home',
      projects: [
        {
          name: 'Maxi Pizza',
          industry: 'Restaurant',
          description: 'In 30 days we increased reach by over 100% and generated strong engagement through authentic content that shows the passion for artisanal pizza.',
          tags: ['+100% reach', '+250% engagement', '45 reels/month'],
        },
        {
          name: 'Atlantis Tours',
          industry: 'Tourism',
          description: 'We created impactful visual content that captures the beauty of Algarve experiences and attracts tourists worldwide.',
          tags: ['+180% bookings', '500K views', 'Global audience'],
        },
        {
          name: 'The Art Studio Tavira',
          industry: 'Art & Workshops',
          description: 'We showcase the creative process behind pottery workshops, connecting local artists with a passionate community.',
          tags: ['+300% workshops', 'Active community', 'Tourists & locals'],
        },
        {
          name: 'Algarve Vacation',
          industry: 'Property Rentals',
          description: 'Premium photography and video content highlighting luxury holiday properties across the Algarve.',
          tags: ['+85% occupancy', 'Direct bookings', 'Positive ROI'],
        },
        {
          name: 'Au Comptoir Beauté',
          industry: 'Beauty Salon',
          description: 'Elegant beauty content designed to attract premium clients and build a sophisticated brand presence.',
          tags: ['+200% followers', 'Fully booked', 'Premium clientele'],
        },
      ],
    },
    
    // Pricing Section
    pricing: {
      badge: 'Strategy',
      title: 'Strategy designed for your brand',
      subtitle: '',
      customTitle: 'Tailored solution',
      cta: 'Create my strategy',
      paragraphs: [
        'At Loom, we don\'t work with fixed plans.',
        'Every brand, business or idea has its own moment, goal and need — and that\'s exactly why we create <strong>tailored solutions for each client</strong>.',
        'Instead of choosing a package, we want to understand your business, your challenges and where you want to go.',
        'From there, we design a clear, simple strategy adapted to your reality.',
      ],
      bullets: [
        'No generic formulas.',
        'No copied solutions.',
        'Just direction designed to grow with consistency.',
      ],
      services: [
        'Content strategy',
        'Social media management',
        'Content production',
        'Paid campaigns (if applicable)',
        'Continuous optimisation',
      ],
    },
    
    // Plans Page
    plansPage: {
      title: 'Choose how you want to grow digitally',
      subtitle: 'Each plan was designed for a different stage of your business.',
      ctaTitle: 'Not sure which to choose?',
      ctaDescription: 'We\'ll talk with you and define the best path for your brand.',
      ctaButton: 'Book a strategy call',
    },
    
    // Form Page
    form: {
      title: 'Let\'s talk about your brand',
      subtitle: 'Fill out this quick form so we can understand how to help you grow with strategy and creativity.',
      timeNote: 'Takes less than 2 minutes',
      next: 'Continue',
      back: 'Back',
      submit: 'Submit',
      steps: {
        name: 'What\'s your name?',
        business: 'What\'s your brand or business name?',
        social: 'Share your Instagram or website',
        location: 'Where are you located?',
        help: 'What do you need help with?',
        budget: 'What\'s your monthly marketing budget?',
        frustration: 'In one sentence, what\'s frustrating you most about your digital presence right now?',
        result: 'What result would make you say "it was worth it"?',
        contact: 'How can we contact you?',
      },
      helpOptions: [
        'Content creation',
        'Social media management',
        'Strategy',
        'Branding and consistency',
        'I don\'t know — I need guidance',
      ],
      budgetOptions: [
        'Less than €350',
        '€350 – €800',
        '€800 – €1,500',
        '€1,500+',
      ],
      emailLabel: 'What\'s your email?',
      phoneLabel: 'What\'s your phone number?',
      thankYou: {
        title: 'Thank you for trusting Loom',
        message: 'If your vision aligns with ours, you\'ll receive a response within 48h with a personalised idea — no pressure, no commitment.',
        cta: 'View portfolio',
      },
    },
    
    // Admin Dashboard
    admin: {
      title: 'Dashboard',
      login: 'Login',
      logout: 'Logout',
      email: 'Email',
      password: 'Password',
      loginTitle: 'Dashboard Access',
      stats: {
        visitsToday: 'Visits today',
        totalVisits: 'Total visits',
        ctaClicks: 'CTA clicks',
        conversionRate: 'Conversion rate',
        totalLeads: 'Total leads',
        avgTime: 'Avg. time',
      },
      filters: {
        last7days: 'Last 7 days',
        last30days: 'Last 30 days',
      },
      leads: 'Leads',
      submissions: 'Submissions',
      viewDetails: 'View details',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('loom-language');
      return saved || 'pt';
    }
    return 'pt';
  });

  useEffect(() => {
    localStorage.setItem('loom-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'pt' ? 'en' : 'pt');
  };

  const setLang = (lang) => {
    if (lang === 'pt' || lang === 'en') {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, toggleLanguage, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
